import { useState } from 'react';
import { useRoomSettings } from '@common/features/roomSettings';
import { useModuleStatuses } from '@features/properties';

type ChildAges = { [child: string]: number };

type Args = {
  childAges: ChildAges;
  childCount: number;
  dispatchAction: (indentifier: string, ages: ChildAges) => void;
};

type Result = {
  childAgesProps?: {
    childAgeValues: ChildAges;
    childCount: number;
    isSubmitted: boolean;
    minChildAge: number;
    maxChildAge: number;
    onChildAgeChange: (ages: { [child: string]: number }) => void;
    errors: string[];
  };
  onCloseGuestPicker: () => void;
  setSubmitted: () => void;
};

function getErrors(childCount: number, ages: { [key: string]: number }): string[] {
  return new Array(childCount)
    .fill({})
    .map((_, i) => `child${i + 1}`)
    .filter((c) => typeof ages[c] !== 'number');
}

export function useChildAges({ childAges, childCount, dispatchAction }: Args): Result {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const setSubmitted = () => setIsSubmitted(true);
  const onCloseGuestPicker = () => setIsSubmitted(false);

  const {
    statuses: { isRoomsEnabled },
  } = useModuleStatuses();

  const { data } = useRoomSettings(isRoomsEnabled);
  const showChildAges = data?.data.showChildAges || false;
  const minChildAge = data?.data.childMinAge || 0;
  const maxChildAge = data?.data.childMaxAge || 16;

  const childAgeValues = new Array(childCount)
    .fill({})
    .map((_, i) => `child${i + 1}`)
    .reduce(
      (acc, current) => ({
        ...acc,
        [current]: typeof childAges[current] === 'number' ? childAges[current] : null,
      }),
      {}
    );

  const onChildAgeChange = (ages: { [child: string]: number }) => {
    dispatchAction('childAges', ages);
  };

  const childAgesProps = showChildAges
    ? {
        childAgeValues,
        childCount,
        isSubmitted,
        minChildAge,
        maxChildAge,
        onChildAgeChange,
        errors: getErrors(childCount, childAges),
      }
    : undefined;

  const childAgesEntries = Object.entries(childAges);
  if (childAgesEntries.length > childCount) {
    dispatchAction('childAges', Object.fromEntries(childAgesEntries.slice(0, childCount)));
  }

  return {
    childAgesProps,
    setSubmitted,
    onCloseGuestPicker,
  };
}
